import { Modal } from "react-bootstrap";
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { BiTransfer } from "react-icons/bi";
import { FaUsb } from "react-icons/fa";

// ---------- Common Constants ----------
const AGENT_URL = "http://127.0.0.1:5000";
const DEVICE_NAME = "Silicon Labs CP210x USB to UART Bridge";
const POLL_INTERVAL = 2000;
const LAST_PORT_KEY = "last_rfid_port";
const LAST_TAB_KEY = "last_rfid_tab";

// ---------- COM Port Device Component ----------
const ComPortDevice = ({ onSuccess }) => {
  // ---------- State Management ----------
  const [ports, setPorts] = useState([]);
  const [selectedPort, setSelectedPort] = useState(
    localStorage.getItem(LAST_PORT_KEY) || ""
  );
  const [epc, setEpc] = useState("");
  const [deviceError, setDeviceError] = useState("");
  const [ScanError, setScanError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState("disconnected"); // 'connected', 'disconnected'

  // ---------- API Wrappers & Core Logic ----------
  const fetchPorts = async () => {
    try {
      const { data } = await axios.get(`${AGENT_URL}/get_ports`);
      return data.success && data.data.ports ? data.data.ports : [];
    } catch {
      return [];
    }
  };

  const checkDeviceConnection = async (portToCheck) => {
    if (!portToCheck) return "disconnected";
    try {
      const { data } = await axios.post(`${AGENT_URL}/select_port`, {
        port: portToCheck,
      });
      return data.success ? "connected" : "disconnected";
    } catch {
      return "disconnected";
    }
  };

  // ---------- Initialization on Component Load ----------
  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      setScanError("");
      const availablePorts = await fetchPorts();
      const normalizedPorts = [...availablePorts].sort((a, b) =>
        a.device.localeCompare(b.device)
      );
      setPorts(normalizedPorts);

      const lastSavedPort = localStorage.getItem(LAST_PORT_KEY);
      let portToUse = null;

      if (
        lastSavedPort &&
        normalizedPorts.some((p) => p.device === lastSavedPort)
      ) {
        portToUse = lastSavedPort;
      } else {
        const preferredPort = normalizedPorts.find((p) =>
          p.description.startsWith(DEVICE_NAME)
        );
        if (preferredPort) {
          portToUse = preferredPort.device;
        }
      }

      if (portToUse) {
        setSelectedPort(portToUse);
        const status = await checkDeviceConnection(portToUse);
        setDeviceStatus(status);
        if (status === "disconnected") {
          setDeviceError("Device is not properly plugged in or connected.");
        }
      } else {
        setDeviceError("No compatible RFID Reader devices detected.");
        setDeviceStatus("disconnected");
        setSelectedPort("");
      }
      setIsLoading(false);
    };
    initialize();
  }, []);

  // ---------- Polling Logic for Device Status and Port List ----------
  useEffect(() => {
    let timer = null;
    let isCancelled = false;

    const poll = async () => {
      if (isScanning || isCancelled) return;

      if (selectedPort) {
        const status = await checkDeviceConnection(selectedPort);
        setDeviceStatus(status);
        if (status === "disconnected") {
          setDeviceError("Device is not properly plugged in or connected.");
        } else {
          setDeviceError("");
        }
      } else {
        const availablePorts = await fetchPorts();
        const normalizedPorts = [...availablePorts].sort((a, b) =>
          a.device.localeCompare(b.device)
        );
        setPorts(normalizedPorts);
        const preferredPort = normalizedPorts.find((p) =>
          p.description.startsWith(DEVICE_NAME)
        );
        if (preferredPort) {
          setSelectedPort(preferredPort.device);
        } else if (normalizedPorts.length > 0) {
          setDeviceError(
            "Preferred RFID Reader not found. Select a port manually."
          );
        }
      }

      timer = setTimeout(poll, POLL_INTERVAL);
    };

    poll();
    return () => {
      isCancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [selectedPort, isScanning]);

  // ---------- Actions ----------
  const handleScan = async () => {
    setEpc("");
    setScanError("");
    if (!selectedPort) {
      setDeviceError("Please select a COM port.");
      return;
    }
    setIsScanning(true);

    try {
      const { data } = await axios.post(`${AGENT_URL}/scan`, {
        port: selectedPort,
        power: 13,
      });
      setIsScanning(false);

      if (data.success) {
        const tags = data.data.tags;
        if (tags && tags.length === 1) {
          const tag = tags[0];
          if (onSuccess) {
            onSuccess(tag);
            localStorage.setItem(LAST_PORT_KEY, selectedPort);
          }
        } else if (tags.length > 1) {
          setScanError("Only one RFID should be scanned. Please try again.");
        } else {
          setScanError("No tags were found.");
        }
      } else {
        setDeviceError(`Scan failed: ${data.message}`);
        setDeviceStatus("disconnected");
      }
    } catch {
      setIsScanning(false);
      setDeviceError(
        "Communication Error: Failed to get a response from the local agent."
      );
      setDeviceStatus("disconnected");
    }
  };

  const handleOnPortChange = async (event) => {
    const port = event.target.value || "";
    setSelectedPort(port);
    if (port) {
      const status = await checkDeviceConnection(port);
      setDeviceStatus(status);
      if (status === "connected") {
        setDeviceError("");
      } else {
        setDeviceError("The selected port is not properly connected.");
      }
    } else {
      setDeviceStatus("disconnected");
    }
  };

  const portOptions = ports.map((port) => (
    <option key={port.device} value={port.device}>
      {`${port.device} - ${port.description}`}
    </option>
  ));

  // ---------- JSX Render Logic ----------
  return (
    <>
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <label htmlFor="port_selection" className="form-label">
            Select Reader COM Port
          </label>
          {deviceStatus === "connected" && (
            <span className="badge bg-success d-flex align-items-center">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-check-circle-fill"
                viewBox="0 0 16 16"
              >
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
              </svg>
              <span className="ms-1">Connected</span>
            </span>
          )}
          {deviceStatus === "disconnected" && (
            <span className="badge bg-danger">Disconnected</span>
          )}
        </div>
        <select
          id="port_selection"
          className="form-select"
          value={selectedPort}
          onChange={handleOnPortChange}
          disabled={isLoading || ports.length === 0}
        >
          <option value="">Select Port</option>
          {portOptions}
        </select>
      </div>

      {deviceError ? (
        <div className="alert alert-danger text-center">{deviceError}</div>
      ) : (
        ScanError && (
          <div className="alert alert-warning text-center">{ScanError}</div>
        )
      )}

      <div className="d-flex justify-content-center mb-3">
        <button
          className="btn btn-primary px-4"
          onClick={handleScan}
          disabled={
            isScanning || deviceStatus === "disconnected" || !selectedPort
          }
        >
          {isScanning ? "Scanning..." : "Scan Tag"}
        </button>
      </div>

      {epc && (
        <div className="alert alert-success text-center">
          <strong>RFID Tag Scanned:</strong> {epc}
        </div>
      )}
    </>
  );
};

// ---------- Plug and Play Device Component ----------
const PlugAndPlayDevice = ({ onSuccess }) => {
  const [scannedKey, setScannedKey] = useState("");
  const scannedValueRef = useRef("");

  useEffect(() => {
    const handleKeyPress = (event) => {
      scannedValueRef.current += event.key;
      if (event.key === "Enter") {
        const tag = scannedValueRef.current.slice(0, -5);
        setScannedKey(tag);
        if (onSuccess) {
          onSuccess(tag);
        }
      }
    };
    document.addEventListener("keypress", handleKeyPress);
    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  }, [onSuccess]);

  return (
    <div className="text-center">
      <p className="fw-bold fs-5">Plug and Play Device</p>
      <p>Make sure your device is plugged in and ready.</p>
      {scannedKey && (
        <div className="alert alert-success mt-3">
          <strong>RDIF Tag:</strong> {scannedKey}
        </div>
      )}
    </div>
  );
};

// ---------- Main Modal Component ----------
const RFIDScannerModal = ({ visible, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState(
    localStorage.getItem(LAST_TAB_KEY) || "com"
  );
  const [agentStatus, setAgentStatus] = useState("unknown");

  const checkAgentStatus = async () => {
    try {
      const { data } = await axios.get(`${AGENT_URL}/status`);
      setAgentStatus(data.success ? "running" : "not_running");
    } catch {
      setAgentStatus("not_running");
    }
  };

  const handleRetryConnection = () => {
    checkAgentStatus();
  };

  const handleTabClick = (tab) => {
    if (activeTab === tab) return;
    setActiveTab(tab);
    localStorage.setItem(LAST_TAB_KEY, tab);
  };

  useEffect(() => {
    if (visible && activeTab === "com") {
      checkAgentStatus();
    }
  }, [visible, activeTab]);

  if (!visible) return null;

  return (
    <Modal show={visible} onHide={onClose} centered>
      <Modal.Header closeButton>
        <div className="d-flex align-items-center gap-2">
          <Modal.Title>Scan RFID</Modal.Title>
          <ul className="nav flex-row custom-nav2">
            <li className="nav-item">
              <span
                className={`cursor-pointer nav-link ${
                  activeTab === "com" ? "active" : ""
                }`}
                style={{ fontSize: "0.75rem" }}
                onClick={() => handleTabClick("com")}
              >
                <BiTransfer className="me-1" /> COM Port
              </span>
            </li>
            <li className="nav-item">
              <span
                className={`cursor-pointer nav-link ${
                  activeTab === "pnp" ? "active" : ""
                }`}
                style={{ fontSize: "0.75rem" }}
                onClick={() => handleTabClick("pnp")}
              >
                <FaUsb className="me-1" /> Plug & Play
              </span>
            </li>
          </ul>
        </div>
      </Modal.Header>
      <Modal.Body>
        {activeTab === "com" &&
          (agentStatus === "running" ? (
            <ComPortDevice
              onSuccess={(tag) => {
                onSuccess(tag);
                onClose();
              }}
            />
          ) : (
            <div className="text-center">
              <div className="alert alert-danger mb-3">
                <strong>Agent Connection Error:</strong>
                <p className="mb-0 mt-2">
                  Could not connect to the Local RFID Agent. Please ensure it is
                  running.
                </p>
              </div>
              <button
                className="btn btn-primary mb-3"
                onClick={handleRetryConnection}
              >
                Try Again
              </button>
            </div>
          ))}

        {activeTab === "pnp" && (
          <PlugAndPlayDevice
            onSuccess={(tag) => {
              onSuccess(tag);
              onClose();
            }}
          />
        )}

        <div className="mt-4">
          <div className="mb-1 fw-bold">How to Scan:</div>
          <ul className="border rounded-2 border-warning p-2 bg-warning bg-opacity-10 text-dark">
            <li>
              <strong>1.</strong> Make sure the device is connected to your
              system.
            </li>
            <li>
              <strong>2.</strong> Place the RFID tag near the reader or move the
              reader over the tag.
            </li>
            {activeTab === "com" && (
              <li>
                <strong>3.</strong> Click the "Scan Tag" button.
              </li>
            )}
          </ul>
        </div>
        {activeTab === "com" && (
          <a
            href={`${window.location.origin}/rfid-reader-details`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-decoration-underline text-primary fs-6 mt-2 align-item-center d-flex justify-content-center"
          >
            View RFID Reader Setup Instructions
          </a>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default RFIDScannerModal;
